/**
 * Asset Delete Confirmation Dialog
 * Confirms deletion of an asset
 */

import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { useDeleteAsset } from '../../stores/asset-list';
import type { Asset } from '../../types/api';
import { toast } from 'sonner';

interface AssetDeleteDialogProps {
  asset: Asset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AssetDeleteDialog({
  asset,
  open,
  onOpenChange,
  onSuccess,
}: AssetDeleteDialogProps) {
  const navigate = useNavigate();
  const deleteMutation = useDeleteAsset();

  async function handleDelete() {
    if (!asset) return;

    try {
      await deleteMutation.mutateAsync(asset.asset_id);
      toast.success('Asset deleted successfully');
      onOpenChange(false);
      onSuccess?.();
      navigate('/assets');
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete asset');
    }
  }

  if (!asset) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Asset?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>{asset.asset_nickname}</strong>{' '}
            (ID: {asset.unique_identifier})? This action will archive the asset and cannot be
            easily undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Asset'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
